import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PartsManagerService } from '../parts-manager.service';
import {
  createDamageSeverity,
  createDamageSeverityFailure,
  createDamageSeveritySuccess,
  createDamageType,
  createDamageTypeFailure,
  createDamageTypeSuccess,
  createPart,
  createPartFailure,
  createPartSuccess,
  deletePart,
  deletePartFailure,
  deletePartsBulk,
  deletePartsBulkFailure,
  deletePartsBulkSuccess,
  deletePartSuccess,
  deleteSeveritiesBulk,
  deleteSeveritiesBulkFailure,
  deleteSeveritiesBulkSuccess,
  deleteSeverity,
  deleteSeverityFailure,
  deleteSeveritySuccess,
  deleteType,
  deleteTypeFailure,
  deleteTypesBulk,
  deleteTypesBulkFailure,
  deleteTypesBulkSuccess,
  deleteTypeSuccess,
  loadAreas,
  loadAreasFailure,
  loadAreasSuccess,
  loadParts,
  loadPartsFailure,
  loadPartsSuccess,
  loadSeverities,
  loadSeveritiesFailure,
  loadSeveritiesSuccess,
  loadTypes,
  loadTypesFailure,
  loadTypesSuccess,
  updatePart,
  updatePartFailure,
  updatePartSuccess,
} from './parts-manager.actions';
import { forkJoin, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { createTranslation } from '../../translations/state/translations.actions';

@Injectable()
export class PartsManagerEffects {
  private readonly action$ = inject(Actions);
  private readonly service = inject(PartsManagerService);

  public readonly loadParts$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadParts),
      switchMap(() =>
        this.service.getParts().pipe(
          map((parts) =>
            loadPartsSuccess({
              parts,
              loading: false,
            })
          ),
          catchError((error) => of(loadPartsFailure({ error, loading: false })))
        )
      )
    )
  );

  public readonly loadTypes$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadTypes),
      switchMap(() =>
        this.service.getDamageTypes().pipe(
          map((types) => loadTypesSuccess({ types, loading: false })),
          catchError((error) => of(loadTypesFailure({ error, loading: false })))
        )
      )
    )
  );

  public readonly loadSeverities$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadSeverities),
      switchMap(() =>
        this.service.getDamageSeverities().pipe(
          map((severities) =>
            loadSeveritiesSuccess({ severities, loading: false })
          ),
          catchError((error) =>
            of(loadSeveritiesFailure({ error, loading: false }))
          )
        )
      )
    )
  );
  public readonly loadAreas$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadAreas),
      switchMap(() =>
        this.service.getDamageAreas().pipe(
          map((areas) => loadAreasSuccess({ areas, loading: false })),
          catchError((error) => of(loadAreasFailure({ error, loading: false })))
        )
      )
    )
  );

  public readonly createPart$ = createEffect(() =>
    this.action$.pipe(
      ofType(createPart),
      switchMap((partBody) =>
        this.service.createPart(partBody).pipe(
          switchMap((part) => [
            createPartSuccess({ part }),
            createTranslation({
              translation: {
                locale: 'en',
                namespace: 'administration.partsManager.parts',
                value: partBody.name,
                key: part.code,
              },
              loading: false,
            }),
          ]),
          catchError((error) => of(createPartFailure({ error })))
        )
      )
    )
  );
  public readonly createDamageType$ = createEffect(() =>
    this.action$.pipe(
      ofType(createDamageType),
      switchMap((damageBody) =>
        this.service.createDamageType(damageBody).pipe(
          switchMap((damageType) => [
            createDamageTypeSuccess({ damageType }),
            createTranslation({
              translation: {
                locale: 'en',
                namespace: 'administration.partsManager.damageTypes',
                value: damageBody.name,
                key: damageType.code,
              },
              loading: false,
            }),
          ]),
          catchError((error) => of(createDamageTypeFailure({ error })))
        )
      )
    )
  );
  public readonly createDamageSeverity$ = createEffect(() =>
    this.action$.pipe(
      ofType(createDamageSeverity),
      switchMap((severityBody) =>
        this.service.createDamageSeverity(severityBody).pipe(
          switchMap((severity) => [
            createDamageSeveritySuccess({ severity }),
            createTranslation({
              translation: {
                locale: 'en',
                namespace: 'administration.partsManager.severities',
                value: severityBody.name,
                key: severity.code,
              },
              loading: false,
            }),
          ]),
          catchError((error) => of(createDamageSeverityFailure({ error })))
        )
      )
    )
  );
  public readonly updatePart$ = createEffect(() =>
    this.action$.pipe(
      ofType(updatePart),
      switchMap(({ id, areaIds, code }) =>
        this.service.updatePart(id, areaIds, code).pipe(
          map((part) => updatePartSuccess(part)),
          catchError((error) => of(updatePartFailure({ error })))
        )
      )
    )
  );
  public readonly deletePart$ = createEffect(() =>
    this.action$.pipe(
      ofType(deletePart),
      switchMap((action) =>
        this.service.deletePart(action).pipe(
          map((deletedPart) => deletePartSuccess({ id: deletedPart.id })),
          catchError((error) => of(deletePartFailure({ error })))
        )
      )
    )
  );
  public readonly deleteType$ = createEffect(() =>
    this.action$.pipe(
      ofType(deleteType),
      switchMap((action) =>
        this.service.deleteType(action).pipe(
          map((deletedType) => deleteTypeSuccess({ id: deletedType.id })),
          catchError((error) => of(deleteTypeFailure({ error })))
        )
      )
    )
  );
  public readonly deleteSeverity$ = createEffect(() =>
    this.action$.pipe(
      ofType(deleteSeverity),
      switchMap((action) =>
        this.service.deleteSeverity(action).pipe(
          map((deletedSeverity) =>
            deleteSeveritySuccess({ id: deletedSeverity.id })
          ),
          catchError((error) => of(deleteSeverityFailure({ error })))
        )
      )
    )
  );
  public readonly deletePartsBulk$ = createEffect(() =>
    this.action$.pipe(
      ofType(deletePartsBulk),
      switchMap(({ parts }) =>
        forkJoin(parts.map((part) => this.service.deletePart(part))).pipe(
          map(() => deletePartsBulkSuccess({ parts })),
          catchError((error) => of(deletePartsBulkFailure({ error })))
        )
      )
    )
  );

  public readonly deleteTypesBulk$ = createEffect(() =>
    this.action$.pipe(
      ofType(deleteTypesBulk),
      switchMap(({ types }) =>
        forkJoin(types.map((type) => this.service.deleteType(type))).pipe(
          map(() => deleteTypesBulkSuccess({ types })),
          catchError((error) => of(deleteTypesBulkFailure({ error })))
        )
      )
    )
  );
  public readonly deleteSeveritiesBulk$ = createEffect(() =>
    this.action$.pipe(
      ofType(deleteSeveritiesBulk),
      switchMap(({ severities }) =>
        forkJoin(
          severities.map((severity) => this.service.deleteSeverity(severity))
        ).pipe(
          map(() => deleteSeveritiesBulkSuccess({ severities })),
          catchError((error) => of(deleteSeveritiesBulkFailure({ error })))
        )
      )
    )
  );
}
